import { notify } from '@/lib/toast';
import { useState, useEffect, Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useSocketEvent } from '@/hooks/useSocketEvent';
import {
  RefreshCw,
  Printer,
  Activity,
  ChevronRight,
  Loader2,
  XCircle,
  CheckCircle,
  Clock,
  AlertTriangle,
  Ban,
  Pencil,
  ShoppingBag,
  RotateCcw,
  Mail,
  MessageCircle,
  CreditCard,
  ArrowLeftRight,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Table, THead, TBody, Tr, Th, Td } from '@/components/ui/table';
import { Card } from '@/components/ui/card';
import { SkeletonTable } from '@/components/ui/skeleton-table';
import { EmptyState } from '@/components/ui/empty-state';
import { TableRowActions } from '@/components/ui/table-row-actions';
import { SearchInput } from '@/components/ui/search-input';
import { StatusBadge } from '@/components/ui/status-badge';
import { paymentMethodLabel } from '@/lib/payment-labels';
import { ManagerApprovalDialog } from '@/components/pos/ManagerApprovalDialog';
import { PaginationBar } from '@/components/ui/pagination-bar';
import { PageHeader } from '@/components/ui/page-header';
import { FilterChips } from '@/components/ui/filter-chips';
import { EntityAutocomplete } from '@/components/ui/entity-autocomplete';
import { Dialog } from 'primereact/dialog';
import { Dropdown } from 'primereact/dropdown';
import api from '@/lib/api';
import { formatCurrency, formatRelativeDate } from '@/lib/utils';
import { cn } from '@/lib/utils';
import { useAuthStore } from '@/stores/auth.store';
import { ReceiptView } from '@/components/pos/ReceiptView';
import toast from 'react-hot-toast';
import { RecordLink } from '@/components/records/RecordLink';
import { printRefundSlip } from '@/lib/refund-slip';
import { useRowOpen } from '@/components/records/useRowOpen';

// ─── Types ───────────────────────────────────────────────────────────────────

interface ProductOption {
  id: string;
  name: string;
  sku?: string | null;
  salePrice?: number | string | null;
}

interface SaleRow {
  id: string;
  invoiceNumber: string;
  status: 'COMPLETED' | 'VOIDED' | 'REFUNDED' | 'HELD';
  subtotal: number;
  discountAmount: number;
  taxAmount: number;
  totalAmount: number;
  paidAmount: number;
  fbrSubmitted: boolean;
  fbrInvoiceId: string | null;
  createdAt: string;
  customer: { id: string; name: string; phone: string | null; email?: string | null } | null;
  payments: { id: string; method: string; amount: number }[];
  _count: { items: number };
  /** POS-I255: first 3 line items for the list row */
  items?: { id: string; productName: string; quantity: number }[];
  isLayaway?: boolean;
  depositAmount?: number;
  voidedAt?: string | null;
  voidedByName?: string | null;
  voidReason?: string | null;
}

interface FbrSubmissionData {
  id: string;
  status: string;
  invoiceNumber: string;
  fbrInvoiceId: string | null;
  errorMessage: string | null;
  attempts: number;
  submittedAt: string | null;
  createdAt: string;
  requestPayload?: Record<string, unknown>;
}

/** Sale detail fields the exchange dialog reads from GET /sales/:id. */
interface ExchangeSaleDetail {
  customerId?: string | null;
  locationId?: string | null;
  items?: { id: string; productName: string; quantity: number | string }[];
}

/** POST /sales/:id/exchange request body. */
interface ExchangePayload {
  returnItems: { saleItemId: string; quantity: number }[];
  exchangeItems: { productId: string; productName: string; quantity: number; unitPrice: number }[];
  notes: string;
  paymentMethod: string;
}

interface SaleItemRaw {
  productDescription?: string;
  hsCode?: string;
  uoM?: string;
  rate?: string;
  saleType?: string;
  sroScheduleNo?: string;
  sroItemSerialNo?: string;
  [key: string]: unknown;
}

interface FbrApiLogRow {
  id: string;
  context: string;
  contextId: string | null;
  environment: string;
  url: string;
  method: string;
  responseStatus: number | null;
  durationMs: number | null;
  createdAt: string;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

const fbrStatusConfig: Record<string, { label: string; icon: React.ElementType; className: string }> = {
  PENDING:   { label: 'fbrStatusPending',   icon: Clock,         className: 'text-warning-text' },
  RETRYING:  { label: 'fbrStatusRetrying',  icon: RefreshCw,     className: 'text-info-text' },
  ACCEPTED:  { label: 'fbrStatusAccepted',  icon: CheckCircle,   className: 'text-success-text' },
  REJECTED:  { label: 'fbrStatusRejected',  icon: XCircle,       className: 'text-destructive-text' },
  FAILED:    { label: 'fbrStatusFailed',    icon: AlertTriangle, className: 'text-destructive-text' },
  SUBMITTED: { label: 'fbrStatusSubmitted', icon: CheckCircle,   className: 'text-success-text' },
};

function getStatusColor(status: number | null) {
  if (!status) return 'text-muted-foreground';
  if (status >= 200 && status < 300) return 'text-success-text';
  if (status >= 400 && status < 500) return 'text-warning-text';
  return 'text-destructive-text';
}

// ─── Province / UOM / rate options ───────────────────────────────────────────

const PROVINCE_OPTIONS = ['Punjab', 'Sindh', 'KPK', 'Balochistan', 'AJK', 'Gilgit Baltistan', 'Islamabad'];
const SCENARIO_OPTIONS = [
  'SN001','SN002','SN003','SN004','SN005','SN006','SN007','SN008','SN009','SN010',
  'SN011','SN012','SN013','SN014','SN015','SN016','SN017','SN018','SN019','SN020',
  'SN021','SN022','SN023','SN024','SN025','SN026','SN027','SN028',
];
const UOM_OPTIONS = [
  'Pcs','KG','MT','Liter','KWH','Meter','Bag','Packs','SET','Gram','Dozen','Pair',
  'Barrels','MMBTU','Square Metre','Square Foot','Numbers, pieces, units',
  'Thousand Unit','Others',
];
const RATE_OPTIONS = ['18%','17%','16%','5%','3%','2%','1.5%','1%','0%',''];

const toDropdownOptions = (values: string[]) => values.map((v) => ({ label: v, value: v }));
const PROVINCE_DD_OPTIONS = toDropdownOptions(PROVINCE_OPTIONS);
const SCENARIO_DD_OPTIONS = toDropdownOptions(SCENARIO_OPTIONS);
const UOM_DD_OPTIONS = toDropdownOptions(UOM_OPTIONS);
const RATE_DD_OPTIONS = toDropdownOptions(RATE_OPTIONS.filter(Boolean));

// ─── Types for editable items ─────────────────────────────────────────────────

interface EditableItem {
  idx: number;
  productDescription: string;
  hsCode: string;
  uoM: string;
  rate: string;
  saleType: string;
  sroScheduleNo: string;
  sroItemSerialNo: string;
}

// ─── FBR Submission Modal ────────────────────────────────────────────────────

const EMPTY_OVERRIDES = {
  sellerProvince: '',
  buyerBusinessName: '',
  buyerNTNCNIC: '',
  buyerProvince: '',
  buyerRegistrationType: '' as '' | 'Registered' | 'Unregistered',
  scenarioId: '',
};

function FbrModal({ sale, onClose }: { sale: SaleRow | null; onClose: () => void }) {
  const { t } = useTranslation('sales');
  const [expandedLogId, setExpandedLogId] = useState<string | null>(null);
  const [editMode, setEditMode] = useState(false);
  const [editForm, setEditForm] = useState(EMPTY_OVERRIDES);
  const [editItems, setEditItems] = useState<EditableItem[]>([]);
  const queryClient = useQueryClient();

  const { data: submissionsData, isLoading: loadingSubmissions } = useQuery({
    queryKey: ['fbr-submission-by-sale', sale?.id],
    queryFn: () =>
      api.get(`/fbr/submissions?saleId=${sale!.id}&limit=10`).then((r) => r.data),
    enabled: !!sale,
    // Poll every 3s while the submission is in a transient state
    refetchInterval: (query) => {
      const status = (query.state.data as { data?: FbrSubmissionData[] } | undefined)?.data?.[0]?.status;
      return status === 'PENDING' || status === 'RETRYING' ? 3000 : false;
    },
  });

  const submissions: FbrSubmissionData[] = submissionsData?.data ?? [];
  const submission = submissions[0] ?? null;

  // Fetch full submission detail to read requestPayload — always loaded so the edit form is ready
  const { data: submissionDetail, isLoading: loadingDetail2 } = useQuery({
    queryKey: ['fbr-submission-detail', submission?.id],
    queryFn: () => api.get(`/fbr/submissions/${submission!.id}`).then((r) => r.data),
    enabled: !!submission,
    // Poll while transient so the modal status updates without a page refresh
    refetchInterval: submission?.status === 'PENDING' || submission?.status === 'RETRYING' ? 3000 : false,
  });

  const { data: logsData, isLoading: loadingLogs } = useQuery({
    queryKey: ['fbr-logs-by-submission', submission?.id],
    queryFn: () =>
      api.get(`/fbr/logs?contextId=${submission!.id}&limit=50`).then((r) => r.data),
    enabled: !!submission,
  });

  const logs: FbrApiLogRow[] = logsData?.data ?? [];

  const { data: logDetail, isLoading: loadingDetail } = useQuery({
    queryKey: ['fbr-log-detail', expandedLogId],
    queryFn: () => api.get(`/fbr/logs/${expandedLogId}`).then((r) => r.data),
    enabled: !!expandedLogId,
  });

  // Pre-fill edit form once submission detail is loaded
  const handleOpenEdit = () => {
    const payload = submissionDetail?.requestPayload ?? submission?.requestPayload ?? {};
    setEditForm({
      sellerProvince: payload.sellerProvince ?? '',
      buyerBusinessName: payload.buyerBusinessName ?? '',
      buyerNTNCNIC: payload.buyerNTNCNIC ?? '',
      buyerProvince: payload.buyerProvince ?? '',
      buyerRegistrationType: payload.buyerRegistrationType ?? '',
      scenarioId: payload.scenarioId ?? '',
    });
    const rawItems: SaleItemRaw[] = Array.isArray(payload.items) ? payload.items : [];
    setEditItems(rawItems.map((it, idx) => ({
      idx,
      productDescription: it.productDescription ?? '',
      hsCode: it.hsCode ?? '',
      uoM: it.uoM ?? '',
      rate: it.rate ?? '',
      saleType: it.saleType ?? '',
      sroScheduleNo: it.sroScheduleNo ?? '',
      sroItemSerialNo: it.sroItemSerialNo ?? '',
    })));
    setEditMode(true);
  };

  const updateItem = (idx: number, field: keyof EditableItem, value: string) => {
    setEditItems((prev) => prev.map((it) => it.idx === idx ? { ...it, [field]: value } : it));
  };

  const retryMutation = useMutation({
    mutationFn: (id: string) => api.post(`/fbr/submissions/${id}/retry`),
    onSuccess: () => {
      toast.success(t('fbrRetryQueued'));
      queryClient.invalidateQueries({ queryKey: ['fbr-submission-by-sale', sale?.id] });
      queryClient.invalidateQueries({ queryKey: ['fbr-submission-detail', submission?.id] });
      queryClient.invalidateQueries({ queryKey: ['fbr-submissions'] });
      queryClient.invalidateQueries({ queryKey: ['sales'] });
    },
    onError: () => toast.error(t('fbrRetryFailed')),
  });

  const retryWithOverridesMutation = useMutation({
    mutationFn: ({ id, overrides, items }: { id: string; overrides: typeof editForm; items: EditableItem[] }) => {
      // Merge editable item fields back into full items from stored payload
      const storedPayload = submissionDetail?.requestPayload ?? submission?.requestPayload ?? {};
      const baseItems: SaleItemRaw[] = Array.isArray(storedPayload.items) ? storedPayload.items : [];
      const mergedItems = baseItems.map((baseItem, idx) => {
        const edited = items.find((it) => it.idx === idx);
        if (!edited) return baseItem;
        return {
          ...baseItem,
          hsCode: edited.hsCode || baseItem.hsCode,
          uoM: edited.uoM || baseItem.uoM,
          rate: edited.rate || baseItem.rate,
          saleType: edited.saleType || baseItem.saleType,
          sroScheduleNo: edited.sroScheduleNo !== undefined ? edited.sroScheduleNo : baseItem.sroScheduleNo,
          sroItemSerialNo: edited.sroItemSerialNo !== undefined ? edited.sroItemSerialNo : baseItem.sroItemSerialNo,
        };
      });
      return api.post(`/fbr/submissions/${id}/retry-with-overrides`, { ...overrides, items: mergedItems }).then((r) => r.data);
    },
    onSuccess: () => {
      toast.success(t('fbrSubmittedWithChanges'));
      setEditMode(false);
      queryClient.invalidateQueries({ queryKey: ['fbr-submission-by-sale', sale?.id] });
      queryClient.invalidateQueries({ queryKey: ['fbr-submission-detail', submission?.id] });
      queryClient.invalidateQueries({ queryKey: ['fbr-logs-by-submission', submission?.id] });
      queryClient.invalidateQueries({ queryKey: ['sales'] });
    },
    onError: (err: unknown) => notify.error(err, t('fbrSubmissionFailed')),
  });

  return (
    <Dialog
      visible={!!sale}
      onHide={onClose}
      header={
        <div>
          <p className="font-semibold">{t('fbrSubmission')}</p>
          <p className="text-sm text-muted-foreground font-normal">{sale?.invoiceNumber}</p>
        </div>
      }
      style={{ width: '52rem' }}
      draggable={false}
      resizable={false}
    >
      {loadingSubmissions ? (
        <div className="flex items-center justify-center py-12 gap-2 text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" />
          {t('loadingFbrData')}
        </div>
      ) : !submission ? (
        <div className="flex flex-col items-center justify-center py-12 text-center">
          <Activity className="h-10 w-10 text-muted-foreground/30 mb-3" />
          <p className="text-sm text-muted-foreground">{t('noFbrSubmissionFound')}</p>
          <p className="text-xs text-muted-foreground/60 mt-1">
            {t('fbrSubmissionsAutoQueued')}
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {/* Submission summary */}
          <div className="rounded-lg border bg-muted/30 p-4 space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                {(() => {
                  const cfg = fbrStatusConfig[submission.status];
                  if (!cfg) return <span className="text-sm font-semibold">{submission.status}</span>;
                  const Icon = cfg.icon;
                  return (
                    <>
                      <Icon className={cn('h-4 w-4', cfg.className)} />
                      <span className={cn('text-sm font-semibold', cfg.className)}>{t(cfg.label)}</span>
                    </>
                  );
                })()}
              </div>
              {(submission.status === 'FAILED' || submission.status === 'REJECTED') && (
                <div className="flex items-center gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={retryMutation.isPending || retryWithOverridesMutation.isPending}
                    onClick={() => retryMutation.mutate(submission.id)}
                    className="h-7 text-xs"
                  >
                    <RefreshCw className="me-1 h-3 w-3" />
                    {t('retry')}
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={retryMutation.isPending || retryWithOverridesMutation.isPending || loadingDetail2}
                    onClick={handleOpenEdit}
                    className="h-7 text-xs border-warning/30 text-warning-text hover:bg-warning/10"
                  >
                    {loadingDetail2 ? <Loader2 className="me-1 h-3 w-3 animate-spin" /> : <Pencil className="me-1 h-3 w-3" />}
                    {t('editAndResubmit')}
                  </Button>
                </div>
              )}
            </div>

            <div className="grid grid-cols-2 gap-3 text-sm">
              <div>
                <p className="text-xs text-muted-foreground">{t('saleId')}</p>
                <p className="font-mono font-medium">{submission.invoiceNumber}</p>
              </div>
              {submission.fbrInvoiceId && (
                <div>
                  <p className="text-xs text-muted-foreground">{t('fbrInvoiceId')}</p>
                  <p className="font-mono font-medium text-success-text">
                    {submission.fbrInvoiceId}
                  </p>
                </div>
              )}
              <div>
                <p className="text-xs text-muted-foreground">{t('attempts')}</p>
                <p className="font-medium">{submission.attempts}</p>
              </div>
              {submission.submittedAt && (
                <div>
                  <p className="text-xs text-muted-foreground">{t('submittedAt')}</p>
                  <p className="font-medium">{new Date(submission.submittedAt).toLocaleString()}</p>
                </div>
              )}
            </div>

            {submission.errorMessage && (
              <div className="rounded-md bg-destructive/10 border border-destructive/30 p-3">
                <p className="text-xs font-semibold text-destructive-text mb-1">{t('error')}</p>
                <p className="text-xs text-destructive-text break-all">{submission.errorMessage}</p>
              </div>
            )}
          </div>

          {/* Edit & Resubmit Form */}
          {editMode && (submission.status === 'FAILED' || submission.status === 'REJECTED') && (
            <div className="rounded-lg border border-warning/30 bg-warning/5 p-4 space-y-3">
              <div className="flex items-center justify-between">
                <p className="text-sm font-semibold text-warning-text flex items-center gap-2">
                  <Pencil className="h-4 w-4" />
                  {t('editInvoiceDetails')}
                </p>
                <button
                  onClick={() => setEditMode(false)}
                  className="text-xs text-muted-foreground hover:text-foreground"
                >
                  {t('cancel')}
                </button>
              </div>
              <p className="text-xs text-muted-foreground">
                {t('overrideDescription')}
              </p>

              {/* Header fields */}
              <p className="text-xs font-semibold text-warning-text mt-1">{t('invoiceHeader')}</p>
              <div className="grid grid-cols-2 gap-3">
                {/* Seller Province */}
                <div className="space-y-1">
                  <Label className="text-xs text-muted-foreground">{t('sellerProvince')}</Label>
                  <Dropdown
                    value={editForm.sellerProvince}
                    onChange={(e) => setEditForm((f) => ({ ...f, sellerProvince: e.value }))}
                    options={[{ label: t('keepCurrent'), value: '' }, ...PROVINCE_DD_OPTIONS]}
                    optionLabel="label"
                    optionValue="value"
                    placeholder={t('keepCurrent')}
                    className="p-dropdown-sm w-full"
                  />
                </div>

                {/* Buyer Province */}
                <div className="space-y-1">
                  <Label className="text-xs text-muted-foreground">{t('buyerProvince')}</Label>
                  <Dropdown
                    value={editForm.buyerProvince}
                    onChange={(e) => setEditForm((f) => ({ ...f, buyerProvince: e.value }))}
                    options={[{ label: t('keepCurrent'), value: '' }, ...PROVINCE_DD_OPTIONS]}
                    optionLabel="label"
                    optionValue="value"
                    placeholder={t('keepCurrent')}
                    className="p-dropdown-sm w-full"
                  />
                </div>

                {/* Buyer Name */}
                <div className="space-y-1">
                  <Label className="text-xs text-muted-foreground">{t('buyerName')}</Label>
                  <Input
                    type="text"
                    value={editForm.buyerBusinessName}
                    onChange={(e) => setEditForm((f) => ({ ...f, buyerBusinessName: e.target.value }))}
                    placeholder={t('leaveBlankToKeepCurrent')}
                    className="h-8 px-2.5"
                  />
                </div>

                {/* Buyer NTN/CNIC */}
                <div className="space-y-1">
                  <Label className="text-xs text-muted-foreground">{t('buyerNtnCnic')}</Label>
                  <Input
                    type="text"
                    value={editForm.buyerNTNCNIC}
                    onChange={(e) => setEditForm((f) => ({ ...f, buyerNTNCNIC: e.target.value }))}
                    placeholder={t('leaveBlankToKeepCurrent')}
                    className="h-8 px-2.5"
                  />
                </div>

                {/* Buyer Registration Type */}
                <div className="space-y-1">
                  <Label className="text-xs text-muted-foreground">{t('buyerType')}</Label>
                  <Dropdown
                    value={editForm.buyerRegistrationType}
                    onChange={(e) => setEditForm((f) => ({ ...f, buyerRegistrationType: e.value as '' | 'Registered' | 'Unregistered' }))}
                    options={[
                      { label: t('keepCurrent'), value: '' },
                      { label: t('registeredNtn'), value: 'Registered' },
                      { label: t('unregisteredCnic'), value: 'Unregistered' },
                    ]}
                    optionLabel="label"
                    optionValue="value"
                    placeholder={t('keepCurrent')}
                    className="p-dropdown-sm w-full"
                  />
                </div>

                {/* Scenario ID */}
                <div className="space-y-1">
                  <Label className="text-xs text-muted-foreground">{t('fbrScenario')}</Label>
                  <Dropdown
                    value={editForm.scenarioId}
                    onChange={(e) => setEditForm((f) => ({ ...f, scenarioId: e.value }))}
                    options={[{ label: t('keepCurrent'), value: '' }, ...SCENARIO_DD_OPTIONS]}
                    optionLabel="label"
                    optionValue="value"
                    placeholder={t('keepCurrent')}
                    className="p-dropdown-sm w-full"
                  />
                </div>
              </div>

              {/* Line Items */}
              {editItems.length > 0 && (
                <>
                  <p className="text-xs font-semibold text-warning-text mt-2">{t('lineItems')}</p>
                  <div className="space-y-3">
                    {editItems.map((item) => (
                      <div key={item.idx} className="rounded-md border border-warning/30 bg-background p-3 space-y-2">
                        <p className="text-xs font-medium truncate">{item.productDescription || `Item ${item.idx + 1}`}</p>
                        <div className="grid grid-cols-2 gap-2">
                          {/* HS Code */}
                          <div className="space-y-1">
                            <Label className="text-xs text-muted-foreground">{t('hsCode')}</Label>
                            <Input
                              type="text"
                              value={item.hsCode}
                              onChange={(e) => updateItem(item.idx, 'hsCode', e.target.value)}
                              placeholder="e.g. 8517.1200"
                              className="h-7 px-2 text-xs font-mono"
                            />
                          </div>
                          {/* UOM */}
                          <div className="space-y-1">
                            <Label className="text-xs text-muted-foreground">{t('uom')}</Label>
                            <Dropdown
                              value={item.uoM}
                              onChange={(e) => updateItem(item.idx, 'uoM', e.value)}
                              options={[{ label: t('keep'), value: '' }, ...UOM_DD_OPTIONS]}
                              optionLabel="label"
                              optionValue="value"
                              placeholder={t('keep')}
                              className="p-dropdown-sm w-full"
                            />
                          </div>
                          {/* Rate */}
                          <div className="space-y-1">
                            <Label className="text-xs text-muted-foreground">{t('taxRate')}</Label>
                            <Dropdown
                              value={item.rate}
                              onChange={(e) => updateItem(item.idx, 'rate', e.value)}
                              options={[{ label: t('keep'), value: '' }, ...RATE_DD_OPTIONS]}
                              optionLabel="label"
                              optionValue="value"
                              placeholder={t('keep')}
                              className="p-dropdown-sm w-full"
                            />
                          </div>
                          {/* Sale Type */}
                          <div className="space-y-1">
                            <Label className="text-xs text-muted-foreground">{t('saleTypeLabel')}</Label>
                            <Input
                              type="text"
                              value={item.saleType}
                              onChange={(e) => updateItem(item.idx, 'saleType', e.target.value)}
                              placeholder="e.g. Goods at standard rate (default)"
                              className="h-7 px-2 text-xs"
                            />
                          </div>
                          {/* SRO Schedule */}
                          <div className="space-y-1">
                            <Label className="text-xs text-muted-foreground">{t('sroScheduleNo')}</Label>
                            <Input
                              type="text"
                              value={item.sroScheduleNo}
                              onChange={(e) => updateItem(item.idx, 'sroScheduleNo', e.target.value)}
                              placeholder="e.g. EIGHTH SCHEDULE Table 1"
                              className="h-7 px-2 text-xs"
                            />
                          </div>
                          {/* SRO Serial */}
                          <div className="space-y-1">
                            <Label className="text-xs text-muted-foreground">{t('sroItemSerial')}</Label>
                            <Input
                              type="text"
                              value={item.sroItemSerialNo}
                              onChange={(e) => updateItem(item.idx, 'sroItemSerialNo', e.target.value)}
                              placeholder="e.g. 2"
                              className="h-7 px-2 text-xs"
                            />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}

              <Button
                size="sm"
                disabled={retryWithOverridesMutation.isPending}
                onClick={() => retryWithOverridesMutation.mutate({ id: submission.id, overrides: editForm, items: editItems })}
                className="w-full bg-warning hover:bg-warning/90 text-warning-foreground"
              >
                {retryWithOverridesMutation.isPending ? (
                  <><Loader2 className="me-2 h-3.5 w-3.5 animate-spin" />{t('submitting')}</>
                ) : (
                  <><RefreshCw className="me-2 h-3.5 w-3.5" />{t('submitWithChanges')}</>
                )}
              </Button>
            </div>
          )}


          <div>
            <p className="text-sm font-semibold mb-2 flex items-center gap-2">
              <Activity className="h-4 w-4 text-muted-foreground" />
              {t('apiCallLogs')}
            </p>
            {loadingLogs ? (
              <div className="flex items-center gap-2 text-xs text-muted-foreground py-3">
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                {t('loadingLogs')}
              </div>
            ) : logs.length === 0 ? (
              <p className="text-xs text-muted-foreground py-2">{t('noApiLogs')}</p>
            ) : (
              <div className="rounded-lg border overflow-hidden">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="border-b bg-muted/30 text-muted-foreground">
                      <th className="px-3 py-2 text-start font-medium">{t('time')}</th>
                      <th className="px-3 py-2 text-start font-medium">{t('environment')}</th>
                      <th className="px-3 py-2 text-start font-medium">{t('status')}</th>
                      <th className="px-3 py-2 text-start font-medium">{t('duration')}</th>
                      <th className="px-3 py-2 w-6" />
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {logs.map((log) => (
                      <Fragment key={log.id}>
                        <tr
                          key={log.id}
                          className="hover:bg-muted/30 cursor-pointer transition-colors"
                          onClick={() => setExpandedLogId(expandedLogId === log.id ? null : log.id)}
                        >
                          <td className="px-3 py-2 text-muted-foreground whitespace-nowrap">
                            {new Date(log.createdAt).toLocaleString(undefined, {
                              dateStyle: 'short',
                              timeStyle: 'medium',
                            })}
                          </td>
                          <td className="px-3 py-2">
                            <span className={cn(
                              'inline-flex items-center rounded-full px-1.5 py-0.5 text-xs font-medium ring-1 ring-inset',
                              log.environment === 'STAGING' && 'bg-warning/10 text-warning-text ring-warning',
                              log.environment === 'LIVE'    && 'bg-success/10 text-success-text ring-success',
                              log.environment === 'OFF'     && 'bg-muted text-muted-foreground ring-border',
                            )}>
                              {log.environment}
                            </span>
                          </td>
                          <td className="px-3 py-2">
                            <span className={cn('font-mono font-semibold', getStatusColor(log.responseStatus))}>
                              {log.responseStatus ?? '—'}
                            </span>
                          </td>
                          <td className="px-3 py-2 text-muted-foreground">
                            {log.durationMs != null ? `${log.durationMs}ms` : '—'}
                          </td>
                          <td className="px-3 py-2">
                            <ChevronRight className={cn(
                              'h-3.5 w-3.5 text-muted-foreground transition-transform',
                              expandedLogId === log.id && 'rotate-90',
                            )} />
                          </td>
                        </tr>
                        {expandedLogId === log.id && (
                          <tr key={`${log.id}-detail`} className="bg-muted/20">
                            <td colSpan={5} className="px-3 py-3">
                              {loadingDetail ? (
                                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                  {t('loadingDetails')}
                                </div>
                              ) : logDetail ? (
                                <div className="space-y-2">
                                  <div>
                                    <p className="text-xs font-semibold text-muted-foreground mb-1">{t('request')}</p>
                                    <p className="font-mono text-xs break-all text-foreground">
                                      <span className="inline-flex items-center rounded px-1 py-0.5 text-xs font-bold bg-muted text-muted-foreground me-2">
                                        {logDetail.method ?? 'POST'}
                                      </span>
                                      {logDetail.url}
                                    </p>
                                  </div>
                                  {logDetail.requestHeaders && (
                                    <div>
                                      <p className="text-xs font-semibold text-muted-foreground mb-1">{t('requestHeaders')}</p>
                                      <pre className="text-xs bg-card text-foreground rounded-lg p-2 overflow-x-auto max-h-32">
                                        {JSON.stringify(logDetail.requestHeaders, null, 2)}
                                      </pre>
                                    </div>
                                  )}
                                  {logDetail.requestBody && (
                                    <div>
                                      <p className="text-xs font-semibold text-muted-foreground mb-1">{t('requestBody')}</p>
                                      <pre className="text-xs bg-card text-foreground rounded-lg p-2 overflow-x-auto max-h-48">
                                        {JSON.stringify(logDetail.requestBody, null, 2)}
                                      </pre>
                                    </div>
                                  )}
                                  {logDetail.responseBody && (
                                    <div>
                                      <p className="text-xs font-semibold text-muted-foreground mb-1">
                                        {t('responseBody')}
                                        <span className={cn('ms-2 font-mono', getStatusColor(logDetail.responseStatus))}>
                                          ({logDetail.responseStatus})
                                        </span>
                                      </p>
                                      <pre className="text-xs bg-card text-foreground rounded-lg p-2 overflow-x-auto max-h-48">
                                        {JSON.stringify(logDetail.responseBody, null, 2)}
                                      </pre>
                                    </div>
                                  )}
                                  {logDetail.responseHeaders && (
                                    <div>
                                      <p className="text-xs font-semibold text-muted-foreground mb-1">{t('responseHeaders')}</p>
                                      <pre className="text-xs bg-card text-foreground rounded-lg p-2 overflow-x-auto max-h-32">
                                        {JSON.stringify(logDetail.responseHeaders, null, 2)}
                                      </pre>
                                    </div>
                                  )}
                                </div>
                              ) : (
                                <p className="text-xs text-muted-foreground">{t('failedToLoadLogDetails')}</p>
                              )}
                            </td>
                          </tr>
                        )}
                      </Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}
    </Dialog>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

type SaleSourceFilter = 'all' | 'pos' | 'restaurant';
const PAYMENT_FILTER_METHODS = ['CASH', 'CARD', 'MOBILE_WALLET', 'QR_CODE', 'BANK_TRANSFER', 'CREDIT', 'CHEQUE', 'GIFT_CARD'] as const;

export function SalesPage() {
  const { t } = useTranslation('sales');
  const rowOpen = useRowOpen(); // POS-I408: a sale opens in place
  const { hasPermission } = useAuthStore();
  const canRead  = hasPermission('pos:read');
  const canVoid  = hasPermission('pos:delete') || hasPermission('pos:*');
  const canRefund = hasPermission('pos:delete') || hasPermission('pos:*');
  const canViewFbr = hasPermission('fbr:read') || hasPermission('fbr:*');
  const [searchParams] = useSearchParams();

  const [search, setSearch]           = useState(searchParams.get('search') ?? '');
  const [filterStatus, setFilterStatus] = useState('');
  // POS-I256: bills from the restaurant exist only with the Restaurant add-on (its permissions are in the plan),
  // so the Source filter is offered, and applied, only when the user has restaurant access.
  const canSeeSource = hasPermission('restaurant:read');
  const [sourceState, setSource]      = useState<SaleSourceFilter>('all');
  const source: SaleSourceFilter = canSeeSource ? sourceState : 'all';
  const [filterPayment, setFilterPayment] = useState('');
  const [filterCashier, setFilterCashier] = useState('');
  const [startDate, setStartDate]     = useState('');
  const [endDate, setEndDate]         = useState('');
  const [page, setPage]               = useState(1);
  const [receiptSaleId, setReceiptSaleId] = useState<string | null>(null);
  const [fbrSale, setFbrSale]         = useState<SaleRow | null>(null);
  const [voidConfirm, setVoidConfirm] = useState<{ open: boolean; id: string; invoiceNumber: string }>({ open: false, id: '', invoiceNumber: '' });
  const [voidReason, setVoidReason]   = useState('');
  const [pendingVoidInvoice, setPendingVoidInvoice] = useState('');
  const [voidManagerOpen, setVoidManagerOpen] = useState(false);
  const [pendingVoidId, setPendingVoidId] = useState<string | null>(null);

  // Send receipt email state
  const [sendReceiptSale, setSendReceiptSale] = useState<SaleRow | null>(null);
  const [receiptEmail, setReceiptEmail] = useState('');
  // POS-I72: a receipt can go by email, WhatsApp or SMS; the customer's email/phone are prefilled.
  const [receiptChannel, setReceiptChannel] = useState<'EMAIL' | 'WHATSAPP' | 'SMS'>('EMAIL');
  const [receiptPhone, setReceiptPhone] = useState('');

  // Layaway payment state
  const [layawaySale, setLayawaySale] = useState<SaleRow | null>(null);
  const [layawayAmount, setLayawayAmount] = useState('');

  // Refund state
  const [refundSaleId, setRefundSaleId]     = useState<string | null>(null);
  const [refundReason, setRefundReason]     = useState('');
  const [refundMethod, setRefundMethod]     = useState<string>(''); // POS-I76: '' = refund the way the customer paid
  const [isFullRefund, setIsFullRefund]     = useState(true);
  const [refundItems, setRefundItems]       = useState<{ saleItemId: string; productName: string; maxQty: number; quantity: number; unitRefund: number }[]>([]);

  // Exchange state
  const [exchangeSaleId, setExchangeSaleId] = useState<string | null>(null);
  const [exchangeReturnItems, setExchangeReturnItems] = useState<{ saleItemId: string; productName: string; maxQty: number; quantity: number }[]>([]);
  const [exchangeNewItems, setExchangeNewItems] = useState<{ productId: string; productName: string; quantity: number; unitPrice: number }[]>([]);
  const [exchangeNotes, setExchangeNotes] = useState('');
  const [exchangePaymentMethod, setExchangePaymentMethod] = useState('CASH');

  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { data: tenantSettings } = useQuery({
    queryKey: ['tenant-settings-sales'],
    queryFn: () => api.get('/tenant/settings').then((r) => r.data as Record<string, unknown>),
    staleTime: 60_000,
  });
  const managerPinEnabled: boolean = Boolean(tenantSettings?.managerPinEnabled);

  useSocketEvent('sale.created', () => {
    queryClient.invalidateQueries({ queryKey: ['sales'] });
  });

  useSocketEvent('sale.voided', () => {
    queryClient.invalidateQueries({ queryKey: ['sales'] });
  });

  useSocketEvent('sale.refunded', () => {
    queryClient.invalidateQueries({ queryKey: ['sales'] });
  });

  useEffect(() => {
    const fromUrl = searchParams.get('search');
    if (fromUrl) {
      setSearch(fromUrl);
      setPage(1);
    }
  }, [searchParams]);

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['sales', search, filterStatus, filterPayment, filterCashier, source, startDate, endDate, page],
    queryFn: async () => {
      const params = new URLSearchParams({ page: String(page), limit: '20', source });
      if (search) params.set('search', search);
      if (filterStatus) params.set('status', filterStatus);
      if (filterPayment) params.set('paymentMethod', filterPayment);
      if (filterCashier) params.set('userId', filterCashier);
      if (startDate) params.set('startDate', startDate);
      if (endDate) params.set('endDate', endDate);
      return api.get(`/sales?${params}`).then((r) => r.data);
    },
    enabled: canRead,
  });

  const hasActiveFilters = Boolean(search || filterStatus || filterPayment || filterCashier || startDate || endDate || source !== 'all');
  const clearFilters = () => {
    setSearch(''); setFilterStatus(''); setFilterPayment(''); setFilterCashier('');
    setSource('all'); setStartDate(''); setEndDate(''); setPage(1);
  };

  // Fetch sale detail to populate refund items
  // POS-I74: always re-read the sale when the dialog opens (a partial refund changes what is left),
  // and treat the cached copy as loading until that fresh read lands.
  const { data: refundSaleDetail, isFetching: refundDetailLoading } = useQuery({
    queryKey: ['sale-detail-refund', refundSaleId],
    queryFn: () => api.get(`/sales/${refundSaleId}`).then((r) => r.data),
    enabled: !!refundSaleId,
    staleTime: 0,
    gcTime: 0,
    refetchOnMount: 'always',
  });

  useEffect(() => {
    if (!refundSaleDetail) return;
    const saleItems = refundSaleDetail.items ?? [];
    setRefundItems(
      saleItems
        .map((item: { id: string; productName: string; quantity: number; remainingQuantity?: number; totalAmount: number | string; refundUnitAmount?: number | string }) => {
          // Per-unit refund mirrors the backend: what the customer really paid (price less the
          // promotion / order-discount share), POS-I73. Older API responses fall back to the price.
          const soldQty = Number(item.quantity) || 1;
          const unitRefund = item.refundUnitAmount !== undefined
            ? Number(item.refundUnitAmount)
            : Number(item.totalAmount) / soldQty;
          const maxQty = Number(item.remainingQuantity ?? item.quantity);
          return {
            saleItemId: item.id,
            productName: item.productName,
            maxQty,
            quantity: maxQty,
            unitRefund,
          };
        })
        .filter((item: { maxQty: number }) => item.maxQty > 0),
    );
  }, [refundSaleDetail]);

  // POS-I69: cashier filter. Needs the staff list; a user who cannot list staff just does not get it.
  const { data: cashierOptions } = useQuery<{ label: string; value: string }[]>({
    queryKey: ['sales-cashier-options'],
    queryFn: () =>
      api.get('/users?limit=200').then((r) => {
        const list: { id: string; firstName?: string; lastName?: string; email?: string }[] = r.data?.data ?? r.data ?? [];
        return list.map((u) => ({
          value: u.id,
          label: [u.firstName, u.lastName].filter(Boolean).join(' ') || u.email || u.id,
        }));
      }),
    retry: false,
  });

  const voidMutation = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) => api.patch(`/sales/${id}/void`, { reason }),
    onSuccess: () => {
      toast.success(t('saleVoided'));
      setVoidReason('');
      queryClient.invalidateQueries({ queryKey: ['sales'] });
      queryClient.invalidateQueries({ queryKey: ['tenant-dashboard'] });
    },
    onError: (err: unknown) => notify.error(err, t('failedToVoidSale')),
  });

  const refundMutation = useMutation({
    mutationFn: ({ id, reason, items, refundMethod }: { id: string; reason: string; items?: { saleItemId: string; quantity: number }[]; refundMethod?: string }) =>
      api.post(`/sales/${id}/refund`, {
        reason,
        // POS-I84: Store credit is a return type, not a disbursement method.
        ...(refundMethod === 'STORE_CREDIT' ? { returnType: 'STORE_CREDIT' } : refundMethod ? { refundMethod } : {}),
        ...(items ? { items } : {}),
      }),
    onSuccess: (res) => {
      // POS-I81: above the refund approval threshold the return only waits for a second approver.
      const out = res?.data as { returnId?: string; returnStatus?: string; returnNumber?: string; storeCreditCode?: string | null; refundAmount?: number } | undefined;
      if (out?.returnStatus === 'PENDING_APPROVAL') {
        toast(t('refundWaitingApproval', { number: out.returnNumber ?? '' }), { duration: 8000 });
      } else if (out?.storeCreditCode) {
        toast.success(t('storeCreditIssued', { code: out.storeCreditCode, amount: formatCurrency(out.refundAmount ?? 0) }), { duration: 10000 });
      } else {
        toast.success(t('saleRefunded'));
      }
      // POS-I406: offer the refund slip / credit note for a refund that went through.
      if (out?.returnId && out.returnStatus !== 'PENDING_APPROVAL') {
        const rid = out.returnId;
        toast((tt) => (
          <span className="flex items-center gap-3 text-sm">
            {out.returnNumber ?? ''}
            <button
              type="button"
              className="rounded-md border px-2 py-1 text-xs font-medium"
              onClick={() => { toast.dismiss(tt.id); void printRefundSlip(rid).then((ok) => { if (!ok) toast.error(t('refundSlipPopup')); }, (err) => notify.error(err, t('refundSlipFailed'))); }}
            >
              {t('printRefundSlip')}
            </button>
          </span>
        ), { duration: 12000 });
      }
      setRefundSaleId(null);
      setRefundReason('');
      setRefundMethod('');
      setIsFullRefund(true);
      setRefundItems([]);
      queryClient.invalidateQueries({ queryKey: ['sales'] });
      queryClient.invalidateQueries({ queryKey: ['sale-detail-refund'] });
      queryClient.invalidateQueries({ queryKey: ['sale-returns'] });
      queryClient.invalidateQueries({ queryKey: ['inventory-products'] });
      queryClient.invalidateQueries({ queryKey: ['tenant-dashboard'] });
      queryClient.invalidateQueries({ queryKey: ['current-shift'] });
    },
    onError: (err: unknown) => notify.error(err, t('failedToRefundSale')),
  });

  const exchangeSaleDetail = useQuery<ExchangeSaleDetail>({
    queryKey: ['sale-detail-exchange', exchangeSaleId],
    queryFn: () => api.get(`/sales/${exchangeSaleId}`).then((r) => r.data),
    enabled: !!exchangeSaleId,
  });

  useEffect(() => {
    if (!exchangeSaleDetail.data) return;
    const items = exchangeSaleDetail.data.items ?? [];
    setExchangeReturnItems(items.map((i) => ({ saleItemId: i.id, productName: i.productName, maxQty: Number(i.quantity), quantity: Number(i.quantity) })));
  }, [exchangeSaleDetail.data]);

  // The customer pays what the server prices (GST included), not the pre-tax catalogue figure.
  const exchangePreviewItems = exchangeNewItems.filter((i) => i.productId).map((i) => ({ productId: i.productId, quantity: i.quantity }));
  const exchangePreview = useQuery<{ totalAmount: number; totalTax: number }>({
    // POS-I447: the exchange is charged for the original sale's customer (price tier) and location, so the preview quotes the same.
    queryKey: ['exchange-preview', exchangePreviewItems, exchangePaymentMethod, exchangeSaleDetail.data?.customerId ?? null, exchangeSaleDetail.data?.locationId ?? null],
    queryFn: () => api.post('/sales/preview', { items: exchangePreviewItems, paymentMethod: exchangePaymentMethod, customerId: exchangeSaleDetail.data?.customerId ?? undefined, locationId: exchangeSaleDetail.data?.locationId ?? undefined }).then((r) => r.data),
    enabled: !!exchangeSaleId && !!exchangeSaleDetail.data && exchangePreviewItems.length > 0,
  });

  const exchangeMutation = useMutation({
    mutationFn: ({ saleId, payload }: { saleId: string; payload: ExchangePayload }) =>
      api.post(`/sales/${saleId}/exchange`, payload).then((r) => r.data),
    onSuccess: (res) => {
      // POS-I83: a return with no new items creates no invoice; the refund is on the return itself.
      toast.success(res.newSale ? `Exchange complete — new invoice ${res.newSale.invoiceNumber}` : `Return recorded — ${res.saleReturn?.returnNumber ?? ''} refunded`);
      setExchangeSaleId(null);
      setExchangeReturnItems([]);
      setExchangeNewItems([]);
      setExchangeNotes('');
      queryClient.invalidateQueries({ queryKey: ['sales'] });
      queryClient.invalidateQueries({ queryKey: ['sale-returns'] });
      queryClient.invalidateQueries({ queryKey: ['inventory-products'] });
      queryClient.invalidateQueries({ queryKey: ['tenant-dashboard'] });
      queryClient.invalidateQueries({ queryKey: ['current-shift'] });
    },
    onError: (err) => notify.error(err, 'Exchange failed'),
  });

  const handleExchangeSubmit = () => {
    if (!exchangeSaleId) return;
    const returnItems = exchangeReturnItems.filter((i) => i.quantity > 0).map((i) => ({ saleItemId: i.saleItemId, quantity: i.quantity }));
    if (returnItems.length === 0) { toast.error('Select at least one item to return'); return; }
    exchangeMutation.mutate({
      saleId: exchangeSaleId,
      payload: { returnItems, exchangeItems: exchangeNewItems, notes: exchangeNotes, paymentMethod: exchangePaymentMethod },
    });
  };

  const [smsPreviewText, setSmsPreviewText] = useState<string | null>(null);
  const closeSendReceipt = () => { setSendReceiptSale(null); setReceiptEmail(''); setReceiptPhone(''); setSmsPreviewText(null); };
  // POS-I229: show the SMS text (frozen shop name, Powered by line) without sending anything.
  const smsPreviewMutation = useMutation({
    mutationFn: ({ id, phone }: { id: string; phone: string }) =>
      api.get(`/sales/${id}/receipt-sms-preview`, { params: phone ? { phone } : {} }).then((r) => (r.data as { message: string }).message),
    onSuccess: (text) => setSmsPreviewText(text),
    onError: (err) => notify.error(err, t('smsPreviewFailed')),
  });
  const openSendReceipt = (sale: SaleRow) => {
    const email = sale.customer?.email ?? '';
    const phone = sale.customer?.phone ?? '';
    setReceiptEmail(email);
    setReceiptPhone(phone);
    setReceiptChannel(email ? 'EMAIL' : phone ? 'WHATSAPP' : 'EMAIL');
    setSendReceiptSale(sale);
  };
  const sendReceiptMutation = useMutation({
    mutationFn: ({ id, channel, email, phone }: { id: string; channel: 'EMAIL' | 'WHATSAPP' | 'SMS'; email: string; phone: string }): Promise<{ channel: 'EMAIL' | 'WHATSAPP' | 'SMS'; sent: boolean; link?: string }> => {
      if (channel === 'WHATSAPP') {
        return api.post(`/whatsapp/invoice/${id}`, { phone }).then((r) => ({ channel, ...(r.data as { sent: boolean; link?: string }) }));
      }
      if (channel === 'SMS') {
        return api.post(`/sales/${id}/send-receipt`, { channel: 'SMS', phone }).then((r) => ({ channel, ...(r.data as { sent: boolean; missingConfig?: string }) }));
      }
      return api.post(`/sales/${id}/send-receipt`, { email }).then(() => ({ channel, sent: true }));
    },
    onSuccess: (res) => {
      if (res.channel === 'WHATSAPP') {
        const link = (res as { link?: string }).link;
        if (!res.sent && link) window.open(link, '_blank', 'noopener');
        toast.success(res.sent ? t('receiptSentWhatsapp') : t('receiptOpeningWhatsapp'));
      } else if (res.channel === 'SMS') {
        if (res.sent) toast.success(t('receiptSentSms'));
        else toast.error(t('receiptSmsNotConfigured'));
        if (!res.sent) return;
      } else {
        toast.success(t('receiptSentSuccess'));
      }
      closeSendReceipt();
    },
    onError: (err) => notify.error(err, t('failedToSendReceipt')),
  });

  const shareWhatsappMutation = useMutation({
    mutationFn: (id: string) =>
      api.post(`/whatsapp/invoice/${id}`).then(
        (r) =>
          r.data as { sent: boolean; link?: string; missingConfig?: string; configPath?: string },
      ),
    onSuccess: (data) => {
      if (data.sent) {
        toast.success('Receipt shared via WhatsApp');
        return;
      }
      if (data.link) window.open(data.link, '_blank', 'noopener');
      if (data.missingConfig && data.configPath) {
        toast((tt) => (
          <span className="flex items-center gap-2">
            WhatsApp API not configured — opened manual share.
            <button
              className="font-medium text-primary-text underline"
              onClick={() => { toast.dismiss(tt.id); navigate(data.configPath!); }}
            >
              Configure
            </button>
          </span>
        ), { icon: '⚙️', duration: 6000 });
      } else {
        toast.success('Opening WhatsApp…');
      }
    },
    onError: (err) => notify.error(err, 'Failed to share via WhatsApp'),
  });

  const layawayPaymentMutation = useMutation({
    mutationFn: ({ id, amount }: { id: string; amount: number }) =>
      api.post(`/sales/${id}/layaway-payment`, { amount }),
    onSuccess: () => {
      toast.success(t('layawayPaymentRecorded'));
      setLayawaySale(null);
      setLayawayAmount('');
      queryClient.invalidateQueries({ queryKey: ['sales'] });
      queryClient.invalidateQueries({ queryKey: ['tenant-dashboard'] });
    },
    onError: (err) => notify.error(err, t('failedToRecordPayment')),
  });

  // Refund total mirrors the backend math (totalAmount prorated by qty). In full mode
  // every returnable unit refunds; in partial mode only the chosen quantities do.
  // POS-I86: refunding everything that is left gives back exactly the sale total (the backend
  // sends the paisa rounding of the header vs the lines; 0 when it does not apply).
  const refundRounding = Number((refundSaleDetail as { refundRoundingAdjustment?: number } | undefined)?.refundRoundingAdjustment ?? 0);
  const refundsEverything = isFullRefund || refundItems.every((i) => i.quantity >= i.maxQty);
  const refundTotal = refundItems.reduce(
    (sum, i) => sum + i.unitRefund * (isFullRefund ? i.maxQty : i.quantity),
    0,
  ) + (refundsEverything && refundItems.length > 0 ? refundRounding : 0);
  const refundHasItems = refundItems.length > 0;
  const canConfirmRefund = refundReason.trim().length > 0 && refundTotal > 0;

  const handleRefundSubmit = () => {
    if (!refundSaleId) return;
    if (!refundReason.trim()) { toast.error(t('refundReasonRequired')); return; }
    if (isFullRefund) {
      refundMutation.mutate({ id: refundSaleId, reason: refundReason, refundMethod });
    } else {
      const partialItems = refundItems
        .filter((i) => i.quantity > 0)
        .map((i) => ({ saleItemId: i.saleItemId, quantity: i.quantity }));
      if (partialItems.length === 0) { toast.error(t('selectAtLeastOneItem')); return; }
      refundMutation.mutate({ id: refundSaleId, reason: refundReason, items: partialItems, refundMethod });
    }
  };

  const sales: SaleRow[]  = data?.data ?? [];
  const total: number     = data?.meta?.total ?? 0;

  if (!canRead) {
    return (
      <div className="flex flex-col items-center justify-center py-32 text-center">
        <Ban className="h-10 w-10 text-muted-foreground/40 mb-3" />
        <p className="text-sm font-medium text-muted-foreground">{t('accessDenied')}</p>
        <p className="text-xs text-muted-foreground/60 mt-1">
          {t('accessDeniedMessage')}
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <PageHeader
        title={t('title')}
        subtitle={t('manageTransactions')}
        action={
          <Button variant="outline" size="sm" onClick={() => refetch()} className="h-8 text-xs">
            <RefreshCw className="me-1.5 h-3 w-3" />
            {t('refresh', { ns: 'common' })}
          </Button>
        }
      />

      {/* Filters */}
      <Card className="p-4">
        <div className="flex flex-wrap gap-3 items-end">
          <div className="flex-1 min-w-48">
            <Label className="text-xs text-muted-foreground mb-1 block">{t('search', { ns: 'common' })}</Label>
            <SearchInput
              value={search}
              onChange={(value) => { setSearch(value); setPage(1); }}
              placeholder={t('invoiceNumberPlaceholder')}
              className="w-full"
            />
          </div>
          <div>
            <Label className="text-xs text-muted-foreground mb-1 block">{t('status')}</Label>
            <Dropdown
              value={filterStatus}
              onChange={(e) => { setFilterStatus(e.value); setPage(1); }}
              options={[
                { label: t('statusAll'), value: '' },
                { label: t('statusCompleted'), value: 'COMPLETED' },
                { label: t('statusVoided'), value: 'VOIDED' },
                { label: t('statusRefunded'), value: 'REFUNDED' },
                { label: t('statusHeld'), value: 'HELD' },
              ]}
              optionLabel="label"
              optionValue="value"
              placeholder={t('statusAll')}
              className="w-44"
            />
          </div>
          {cashierOptions && cashierOptions.length > 0 && (
            <div>
              <Label className="text-xs text-muted-foreground mb-1 block">{t('cashier')}</Label>
              <Dropdown
                value={filterCashier}
                onChange={(e) => { setFilterCashier(e.value); setPage(1); }}
                options={[{ label: t('allCashiers'), value: '' }, ...cashierOptions]}
                optionLabel="label"
                optionValue="value"
                placeholder={t('allCashiers')}
                className="w-48"
              />
            </div>
          )}
          <div>
            <Label className="text-xs text-muted-foreground mb-1 block">{t('paymentMethod')}</Label>
            <Dropdown
              value={filterPayment}
              onChange={(e) => { setFilterPayment(e.value); setPage(1); }}
              options={[
                { label: t('paymentMethodAll'), value: '' },
                ...PAYMENT_FILTER_METHODS.map((m) => ({ label: paymentMethodLabel(m), value: m })),
              ]}
              optionLabel="label"
              optionValue="value"
              placeholder={t('paymentMethodAll')}
              className="w-44"
            />
          </div>
          {canSeeSource && (
          <div className="min-w-0">
            <Label className="text-xs text-muted-foreground mb-1 block">{t('source')}</Label>
            <FilterChips
              chips={[
                { key: 'all', label: t('sourceAll') },
                { key: 'pos', label: t('sourcePos') },
                { key: 'restaurant', label: t('sourceRestaurant') },
              ]}
              value={source}
              onChange={(key) => { setSource(key as SaleSourceFilter); setPage(1); }}
              variant="outline"
              size="sm"
            />
          </div>
          )}
          <div>
            <Label className="text-xs text-muted-foreground mb-1 block">{t('filterFrom')}</Label>
            <Input
              type="date"
              value={startDate}
              onChange={(e) => { setStartDate(e.target.value); setPage(1); }}
              className="h-8 text-sm w-36"
            />
          </div>
          <div>
            <Label className="text-xs text-muted-foreground mb-1 block">{t('filterTo')}</Label>
            <Input
              type="date"
              value={endDate}
              onChange={(e) => {
                if (startDate && e.target.value && new Date(e.target.value) < new Date(startDate)) {
                  toast.error(t('endDateError'));
                  return;
                }
                setEndDate(e.target.value); setPage(1);
              }}
              className="h-8 text-sm w-36"
            />
          </div>
          <span className="text-xs text-muted-foreground pb-1.5">{total} {total === 1 ? t('record') : t('records')}</span>
        </div>
      </Card>

      {/* Table */}
      <Card className="overflow-hidden">
        {isLoading ? (
          <div className="p-6">
            <SkeletonTable rows={8} cols={6} />
          </div>
        ) : sales.length === 0 && hasActiveFilters ? (
          // POS-I70: filters that match nothing are not an empty history.
          <EmptyState
            icon={ShoppingBag}
            title={t('noSalesMatch')}
            description={t('noSalesMatchSubtitle')}
            action={{ label: t('clearFilters'), onClick: clearFilters }}
          />
        ) : sales.length === 0 ? (
          <EmptyState
            icon={ShoppingBag}
            title={t('noSales')}
            description={t('noSalesSubtitle')}
            action={{ label: t('goToPOS'), onClick: () => navigate('/pos') }}
          />
        ) : (
          <Table mobileCards>
              <THead>
                <Tr hover={false}>
                  <Th>{t('invoice')}</Th>
                  <Th>{t('customer')}</Th>
                  <Th>{t('items')}</Th>
                  <Th className="text-end">{t('total')}</Th>
                  <Th>{t('paymentMethod')}</Th>
                  <Th className="text-end">{t('tax')}</Th>
                  <Th className="text-center">{t('status')}</Th>
                  {canViewFbr && (
                    <Th className="text-center">{t('fbr')}</Th>
                  )}
                  <Th>{t('date')}</Th>
                  <Th className="text-end">{t('actions')}</Th>
                </Tr>
              </THead>
              <TBody>
                {sales.map((sale) => (
                  <Tr key={sale.id} {...rowOpen('invoice', sale.id, sale.invoiceNumber)}>
                    <Td className="td-name">
                      <RecordLink type="invoice" id={sale.id} label={sale.invoiceNumber} variant="mono" className="text-xs font-semibold" />
                    </Td>
                    <Td className="text-sm" label={t('customer')}>
                      {sale.customer ? (
                        <div>
                          <p className="font-medium leading-tight">{sale.customer.name}</p>
                          {sale.customer.phone && (
                            <p className="text-xs text-muted-foreground">{sale.customer.phone}</p>
                          )}
                        </div>
                      ) : (
                        <span className="text-muted-foreground text-xs">{t('walkIn')}</span>
                      )}
                    </Td>
                    <Td
                      className="text-start text-muted-foreground text-xs max-w-56"
                      label={t('items')}
                      title={(sale.items ?? []).map((i) => `${i.productName} × ${i.quantity}`).join('\n')
                        + (sale._count.items > (sale.items?.length ?? 0) ? `\n${t('moreItems', { count: sale._count.items - (sale.items?.length ?? 0) })}` : '')}
                    >
                      {(sale.items ?? []).length > 0 ? (
                        <span className="line-clamp-2 break-words">
                          {(sale.items ?? []).map((i) => i.productName).join(', ')}
                          {sale._count.items > (sale.items?.length ?? 0) && (
                            <span className="whitespace-nowrap"> {t('moreItems', { count: sale._count.items - (sale.items?.length ?? 0) })}</span>
                          )}
                        </span>
                      ) : (
                        sale._count.items
                      )}
                    </Td>
                    <Td className="text-end font-mono text-xs font-semibold" label={t('total')}>
                      {formatCurrency(Number(sale.totalAmount))}
                    </Td>
                    <Td className="text-xs text-muted-foreground td-hide">
                      {[
                        ...new Set(
                          (sale.payments ?? [])
                            .filter((p) => Number(p.amount) > 0)
                            .map((p) => paymentMethodLabel(p.method)),
                        ),
                      ].join(' + ') || '—'}
                    </Td>
                    <Td className="text-end font-mono text-xs text-muted-foreground td-hide">
                      {formatCurrency(Number(sale.taxAmount))}
                    </Td>
                    <Td className="text-center" label={t('status')}>
                      <StatusBadge status={sale.status} />
                      {sale.status === 'VOIDED' && (sale.voidReason || sale.voidedAt) && (
                        <p
                          className="mt-0.5 text-[11px] leading-tight text-muted-foreground max-w-40 mx-auto"
                          title={[sale.voidReason, sale.voidedByName, sale.voidedAt ? new Date(sale.voidedAt).toLocaleString() : null].filter(Boolean).join(' · ')}
                        >
                          {[sale.voidReason, sale.voidedByName].filter(Boolean).join(' · ')}
                          {sale.voidedAt && <span className="block">{formatRelativeDate(sale.voidedAt)}</span>}
                        </p>
                      )}
                    </Td>
                    {canViewFbr && (
                      <Td className="text-center td-hide">
                        {sale.status === 'VOIDED' ? (
                          <span className="text-muted-foreground" title={t('fbrVoidedTooltip')}>—</span>
                        ) : sale.fbrSubmitted ? (
                          <span title={sale.fbrInvoiceId ? `${t('fbrSubmittedTooltip')} — ${sale.fbrInvoiceId}` : t('fbrSubmittedTooltip')}>
                            <CheckCircle className="h-4 w-4 text-success-text mx-auto" />
                          </span>
                        ) : (
                          <span title={t('fbrPendingOrNotSubmitted')}>
                            <Clock className="h-4 w-4 text-warning-text mx-auto" />
                          </span>
                        )}
                      </Td>
                    )}
                    <Td className="text-xs text-muted-foreground whitespace-nowrap td-hide" title={new Date(sale.createdAt).toLocaleString()}>
                      {formatRelativeDate(sale.createdAt)}
                    </Td>
                    <Td className="td-actions" onClick={(e) => e.stopPropagation()}>
                      <TableRowActions
                        actions={[
                          {
                            key: 'receipt',
                            label: t('printReceipt'),
                            icon: Printer,
                            onSelect: () => setReceiptSaleId(sale.id),
                            primary: true,
                          },
                          {
                            key: 'email',
                            label: t('sendReceiptByEmail'),
                            icon: Mail,
                            onSelect: () => openSendReceipt(sale),
                            primary: true,
                          },
                          {
                            key: 'whatsapp',
                            label: 'Share receipt via WhatsApp',
                            icon: MessageCircle,
                            onSelect: () => shareWhatsappMutation.mutate(sale.id),
                            permission: !!sale.customer?.phone,
                            disabled: shareWhatsappMutation.isPending,
                          },
                          {
                            key: 'fbr',
                            label: t('viewFbrSubmission'),
                            icon: Activity,
                            onSelect: () => setFbrSale(sale),
                            permission: canViewFbr,
                          },
                          {
                            key: 'void',
                            label: t('voidSale'),
                            icon: XCircle,
                            onSelect: () => {
                              setVoidReason('');
                              if (managerPinEnabled) {
                                setPendingVoidId(sale.id);
                                setPendingVoidInvoice(sale.invoiceNumber);
                                setVoidManagerOpen(true);
                              } else {
                                setVoidConfirm({ open: true, id: sale.id, invoiceNumber: sale.invoiceNumber });
                              }
                            },
                            variant: 'destructive',
                            permission: canVoid && sale.status === 'COMPLETED',
                            disabled: voidMutation.isPending,
                          },
                          {
                            key: 'refund',
                            label: t('refundSaleTooltip'),
                            icon: RotateCcw,
                            onSelect: () => { setRefundSaleId(sale.id); setIsFullRefund(true); setRefundReason(''); setRefundItems([]); },
                            permission: canRefund && sale.status === 'COMPLETED',
                          },
                          {
                            key: 'exchange',
                            label: 'Exchange items',
                            icon: ArrowLeftRight,
                            onSelect: () => { setExchangeSaleId(sale.id); setExchangeNewItems([]); setExchangeNotes(''); setExchangePaymentMethod('CASH'); },
                            permission: canRefund && sale.status === 'COMPLETED',
                          },
                          {
                            key: 'layaway-pay',
                            label: t('pay'),
                            icon: CreditCard,
                            onSelect: () => { setLayawaySale(sale); setLayawayAmount(''); },
                            permission: sale.status === 'HELD' && sale.isLayaway,
                            labeled: true,
                          },
                        ]}
                      />
                    </Td>
                  </Tr>
                ))}
              </TBody>
          </Table>
        )}
      </Card>

      {/* Pagination */}
      {total > 0 && (
        <PaginationBar
          page={page}
          pageSize={20}
          total={total}
          onPageChange={setPage}
        />
      )}

      {/* Receipt Dialog (reuses existing ReceiptView component) */}
      <ReceiptView saleId={receiptSaleId} onClose={() => setReceiptSaleId(null)} />

      {/* FBR Submission + API Logs Modal */}
      <FbrModal sale={fbrSale} onClose={() => setFbrSale(null)} />

      {/* Void Confirm Dialog */}
      <Dialog
        visible={voidConfirm.open}
        onHide={() => setVoidConfirm({ open: false, id: '', invoiceNumber: '' })}
        header={<span>{t('voidSale')}</span>}
        footer={
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setVoidConfirm({ open: false, id: '', invoiceNumber: '' })}>
              {t('cancel', { ns: 'common' })}
            </Button>
            <Button
              variant="destructive"
              disabled={voidMutation.isPending || voidReason.trim().length < 3}
              onClick={() => {
                voidMutation.mutate({ id: voidConfirm.id, reason: voidReason.trim() });
                setVoidConfirm({ open: false, id: '', invoiceNumber: '' });
              }}
            >
              {t('voidSale')}
            </Button>
          </div>
        }
      >
        <div className="space-y-3">
          <p className="text-sm">{t('voidConfirmMsg', { invoice: voidConfirm.invoiceNumber })}</p>
          <div>
            <Label className="mb-1.5 block">{t('voidReasonLabel')} <span className="text-destructive-text">*</span></Label>
            <Input
              value={voidReason}
              onChange={(e) => setVoidReason(e.target.value)}
              placeholder={t('voidReasonPlaceholder')}
              maxLength={500}
              className="h-9"
            />
          </div>
        </div>
      </Dialog>

      {/* Manager Approval Dialog for void (T-001) */}
      <ManagerApprovalDialog
        open={voidManagerOpen}
        reason="Voiding a sale requires manager authorisation."
        onApproved={(manager) => {
          setVoidManagerOpen(false);
          toast.success(`Approved by ${manager.name}`);
          if (pendingVoidId) setVoidConfirm({ open: true, id: pendingVoidId, invoiceNumber: pendingVoidInvoice });
          setPendingVoidId(null);
        }}
        onCancel={() => { setVoidManagerOpen(false); setPendingVoidId(null); }}
      />

      {/* Refund Dialog */}
      <Dialog
        visible={!!refundSaleId}
        onHide={() => { setRefundSaleId(null); setRefundReason(''); setIsFullRefund(true); setRefundItems([]); }}
        header={
          <div className="flex items-center gap-2">
            <RotateCcw className="h-4 w-4 text-warning-text" />
            <span>{t('refundSale')}</span>
            {refundSaleDetail?.invoiceNumber && (
              <span className="font-mono text-sm text-muted-foreground">· {refundSaleDetail.invoiceNumber}</span>
            )}
          </div>
        }
        footer={
          <div className="flex items-center justify-between gap-2">
            <span className="text-sm text-muted-foreground">
              {!refundDetailLoading && (
                <>{t('refundTotalLabel')}: <span className="font-semibold text-foreground">{formatCurrency(refundTotal)}</span></>
              )}
            </span>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => { setRefundSaleId(null); setRefundReason(''); }}>
                {t('cancel', { ns: 'common' })}
              </Button>
              <Button
                onClick={handleRefundSubmit}
                disabled={refundMutation.isPending || !canConfirmRefund}
                className="bg-warning hover:bg-warning/90 text-warning-foreground"
              >
                {refundMutation.isPending && <Loader2 className="me-1.5 h-3.5 w-3.5 animate-spin" />}
                {t('confirmRefund')}
              </Button>
            </div>
          </div>
        }
        style={{ width: '36rem', maxWidth: '95vw' }}
        draggable={false}
        resizable={false}
      >
        <div className="space-y-4 py-1">
          {refundDetailLoading ? (
            <SkeletonTable rows={3} cols={4} />
          ) : (
          <>
          <div>
            <Label className="mb-1.5 block">{t('refundReasonLabel')} <span className="text-destructive-text">*</span></Label>
            <Input
              value={refundReason}
              onChange={(e) => setRefundReason(e.target.value)}
              placeholder={t('refundReasonPlaceholder')}
              className="h-9"
            />
          </div>

          <div>
            <Label className="mb-1.5 block">{t('refundMethodLabel')}</Label>
            <Dropdown
              value={refundMethod}
              onChange={(e) => setRefundMethod(e.value)}
              options={[
                { label: t('refundMethodOriginal'), value: '' },
                { label: t('refundMethodCash'), value: 'CASH' },
                { label: t('refundMethodCard'), value: 'CARD' },
                { label: t('refundMethodBank'), value: 'BANK_TRANSFER' },
                { label: t('refundMethodWallet'), value: 'MOBILE_WALLET' },
                // POS-I84: keep the money as a gift card in the customer's name (needs a customer).
                ...(refundSaleDetail?.customerId
                  ? [{ label: t('refundAsStoreCredit'), value: 'STORE_CREDIT' }]
                  : []),
              ]}
              optionLabel="label"
              optionValue="value"
              className="w-full"
            />
            <p className="text-xs text-muted-foreground mt-1">{t('refundMethodHint')}</p>
          </div>

          <div className="flex items-center gap-3" role="radiogroup" aria-label={t('refundSale')}>
            <button
              type="button"
              role="radio"
              aria-checked={isFullRefund}
              onClick={() => setIsFullRefund(true)}
              className={`flex-1 rounded-lg border py-2 text-sm font-medium transition-colors ${isFullRefund ? 'bg-warning/10 border-warning/30 text-warning-text' : 'bg-background text-muted-foreground hover:bg-accent'}`}
            >
              {t('fullRefund')}
            </button>
            <button
              type="button"
              role="radio"
              aria-checked={!isFullRefund}
              onClick={() => setIsFullRefund(false)}
              className={`flex-1 rounded-lg border py-2 text-sm font-medium transition-colors ${!isFullRefund ? 'bg-warning/10 border-warning/30 text-warning-text' : 'bg-background text-muted-foreground hover:bg-accent'}`}
            >
              {t('partialRefund')}
            </button>
          </div>

          {refundHasItems ? (
            <>
            <div className="rounded-lg border overflow-hidden">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border bg-muted/50">
                    <th className="px-4 py-3 text-start text-xs font-semibold uppercase tracking-wide text-muted-foreground">{t('product')}</th>
                    <th className="px-4 py-3 text-end text-xs font-semibold uppercase tracking-wide text-muted-foreground">{t('maxQty')}</th>
                    <th className="px-4 py-3 text-end text-xs font-semibold uppercase tracking-wide text-muted-foreground">{t('refundQty')}</th>
                    <th className="px-4 py-3 text-end text-xs font-semibold uppercase tracking-wide text-muted-foreground">{t('amount')}</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {refundItems.map((item, idx) => {
                    const lineQty = isFullRefund ? item.maxQty : item.quantity;
                    return (
                      <tr key={item.saleItemId}>
                        <td className="px-4 py-3">{item.productName}</td>
                        <td className="px-4 py-3 text-end text-muted-foreground">{item.maxQty}</td>
                        <td className="px-4 py-3 text-end">
                          {isFullRefund ? (
                            <span className="text-muted-foreground">{item.maxQty}</span>
                          ) : (
                            <Input
                              type="number"
                              min={0}
                              max={item.maxQty}
                              step={1}
                              value={item.quantity}
                              onChange={(e) => {
                                const v = Math.min(item.maxQty, Math.max(0, Number(e.target.value)));
                                setRefundItems((prev) => prev.map((ri, i) => i === idx ? { ...ri, quantity: v } : ri));
                              }}
                              className="w-20 h-7 px-2 text-end"
                            />
                          )}
                        </td>
                        <td className="px-4 py-3 text-end font-mono">{formatCurrency(item.unitRefund * lineQty)}</td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr className="border-t bg-muted/20 font-medium">
                    <td className="px-4 py-3" colSpan={3}>{t('refundTotalLabel')}</td>
                    <td className="px-4 py-3 text-end font-mono">{formatCurrency(refundTotal)}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
            {refundTotal === 0 && (
              <p className="text-xs text-muted-foreground">{t('refundNothingSelectedHint')}</p>
            )}
            </>
          ) : (
            <p className="rounded-lg border border-dashed px-3 py-4 text-center text-sm text-muted-foreground">
              {t('noItemsToRefund')}
            </p>
          )}
          </>
          )}
        </div>
      </Dialog>
      <Dialog
        visible={!!sendReceiptSale}
        onHide={closeSendReceipt}
        header={
          <div className="flex items-center gap-2">
            <Mail className="h-4 w-4 text-primary-text" />
            <span>{t('sendReceiptDialogTitle')}</span>
          </div>
        }
        footer={
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={closeSendReceipt}>{t('cancel')}</Button>
            <Button
              onClick={() => {
                if (receiptChannel === 'EMAIL') {
                  if (!receiptEmail || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(receiptEmail)) { toast.error(t('validEmailRequired')); return; }
                } else if (receiptPhone.replace(/\D/g, '').length < 7) {
                  toast.error(t('validPhoneRequired')); return;
                }
                sendReceiptMutation.mutate({ id: sendReceiptSale!.id, channel: receiptChannel, email: receiptEmail.trim(), phone: receiptPhone.trim() });
              }}
              disabled={sendReceiptMutation.isPending}
            >
              {sendReceiptMutation.isPending && <Loader2 className="me-1.5 h-3.5 w-3.5 animate-spin" />}
              {t('sendReceipt')}
            </Button>
          </div>
        }
        style={{ width: '28rem', maxWidth: '95vw' }}
        draggable={false}
        resizable={false}
      >
        <div className="space-y-4 py-1">
          {sendReceiptSale && (
            <div className="rounded-lg border bg-muted/30 px-3 py-2 text-sm">
              <p className="text-muted-foreground text-xs mb-0.5">{t('sale')}</p>
              <p className="font-mono font-semibold">{sendReceiptSale.invoiceNumber}</p>
              {sendReceiptSale.customer && <p className="text-xs text-muted-foreground mt-0.5">{sendReceiptSale.customer.name}</p>}
            </div>
          )}
          <FilterChips
            chips={[
              { key: 'EMAIL', label: t('receiptChannelEmail') },
              { key: 'WHATSAPP', label: t('receiptChannelWhatsapp') },
              { key: 'SMS', label: t('receiptChannelSms') },
            ]}
            value={receiptChannel}
            onChange={(key) => { setReceiptChannel(key as 'EMAIL' | 'WHATSAPP' | 'SMS'); setSmsPreviewText(null); }}
            variant="outline"
            size="sm"
          />
          {receiptChannel === 'EMAIL' ? (
            <div>
              <Label className="mb-1.5 block">{t('emailAddress')} <span className="text-destructive-text">*</span></Label>
              <Input
                type="email"
                value={receiptEmail}
                onChange={(e) => setReceiptEmail(e.target.value)}
                placeholder="customer@example.com"
                className="h-9"
                autoFocus
              />
            </div>
          ) : (
            <div>
              <Label className="mb-1.5 block">{t('receiptPhoneLabel')} <span className="text-destructive-text">*</span></Label>
              <Input
                type="tel"
                value={receiptPhone}
                onChange={(e) => setReceiptPhone(e.target.value)}
                placeholder="03xx xxxxxxx"
                className="h-9"
                autoFocus
              />
            </div>
          )}
          {receiptChannel === 'SMS' && (
            <div className="space-y-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => smsPreviewMutation.mutate({ id: sendReceiptSale!.id, phone: receiptPhone.trim() })}
                disabled={smsPreviewMutation.isPending}
              >
                {smsPreviewMutation.isPending && <Loader2 className="me-1.5 h-3.5 w-3.5 animate-spin" />}
                {t('previewSmsText')}
              </Button>
              {smsPreviewText != null && (
                <div className="rounded-lg border bg-muted/30 px-3 py-2 text-sm" data-testid="sms-preview">
                  <p className="text-xs text-muted-foreground mb-1">{t('smsPreviewHint', { count: smsPreviewText.length })}</p>
                  <p className="whitespace-pre-wrap break-words">{smsPreviewText}</p>
                </div>
              )}
            </div>
          )}
        </div>
      </Dialog>

      {/* Exchange Dialog */}
      <Dialog
        visible={!!exchangeSaleId}
        onHide={() => { setExchangeSaleId(null); setExchangeReturnItems([]); setExchangeNewItems([]); setExchangeNotes(''); }}
        header={<div className="flex items-center gap-2"><ArrowLeftRight className="h-4 w-4 text-primary-text" /><span>Exchange Items</span></div>}
        footer={
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setExchangeSaleId(null)}>Cancel</Button>
            <Button onClick={handleExchangeSubmit} disabled={exchangeMutation.isPending}>
              {exchangeMutation.isPending ? 'Processing…' : 'Process Exchange'}
            </Button>
          </div>
        }
        modal breakpoints={{ '960px': '80vw', '640px': '95vw' }} style={{ width: '60vw' }}
      >
        {exchangeSaleDetail.isLoading ? (
          <div className="py-8 text-center text-sm text-muted-foreground">Loading sale items…</div>
        ) : (
          <div className="space-y-5 pt-2">
            {/* Step 1: Items to Return */}
            <div>
              <p className="text-sm font-semibold mb-2">Step 1: Items to return (select qty)</p>
              <div className="space-y-2">
                {exchangeReturnItems.map((item, idx) => (
                  <div key={item.saleItemId} className="flex items-center justify-between gap-3 rounded-lg border px-3 py-2">
                    <span className="text-sm flex-1 truncate">{item.productName}</span>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-muted-foreground">Max: {item.maxQty}</span>
                      <Input
                        type="number" min={0} max={item.maxQty}
                        value={item.quantity}
                        onChange={(e) => setExchangeReturnItems((prev) => prev.map((p, i) => i === idx ? { ...p, quantity: Math.min(item.maxQty, Math.max(0, Number(e.target.value))) } : p))}
                        className="w-16 h-auto px-2 py-1 text-end"
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
            {/* Step 2: New Items */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm font-semibold">Step 2: New items to issue (optional)</p>
                <Button size="sm" variant="outline" onClick={() => setExchangeNewItems((prev) => [...prev, { productId: '', productName: '', quantity: 1, unitPrice: 0 }])}>
                  + Add Item
                </Button>
              </div>
              {exchangeNewItems.length === 0 ? (
                <p className="text-xs text-muted-foreground text-center py-3 border rounded-lg">No new items — this will be a return only</p>
              ) : (
                <div className="space-y-2">
                  {exchangeNewItems.map((item, idx) => (
                    <div key={idx} className="grid grid-cols-12 gap-2 items-center">
                      <div className="col-span-8">
                        <EntityAutocomplete<ProductOption>
                          endpoint="/products"
                          value={item.productId ? { id: item.productId, name: item.productName } : null}
                          onChange={(product) => setExchangeNewItems((prev) => prev.map((p, i) => i === idx ? {
                            ...p,
                            productId: product?.id ?? '',
                            productName: product?.name ?? '',
                            unitPrice: product && (!p.unitPrice || p.unitPrice === 0) ? Number(product.salePrice ?? 0) : p.unitPrice,
                          } : p))}
                          getLabel={(p) => p.name}
                          itemTemplate={(p) => (
                            <div className="flex flex-col py-0.5">
                              <span className="font-medium text-sm">{p.name}</span>
                              <span className="text-xs text-muted-foreground">SKU: {p.sku ?? '—'} · {formatCurrency(Number(p.salePrice ?? 0))}</span>
                            </div>
                          )}
                          placeholder="Search product by name or SKU…"
                          inputClassName="w-full h-8 rounded-md border bg-background px-2 text-sm"
                        />
                      </div>
                      <Input type="number" min={1} placeholder="Qty" value={item.quantity} onChange={(e) => setExchangeNewItems((prev) => prev.map((p, i) => i === idx ? { ...p, quantity: Math.max(1, Number(e.target.value)) } : p))} className="col-span-2 h-8 px-2 text-end" />
                      <Input type="number" min={0} step="0.01" placeholder="Price" value={item.unitPrice} readOnly title="Priced from the catalogue" className="col-span-1 h-8 px-2 text-end" />
                      <button onClick={() => setExchangeNewItems((prev) => prev.filter((_, i) => i !== idx))} className="col-span-1 h-8 rounded-md text-destructive-text hover:bg-destructive/10 text-xs">✕</button>
                    </div>
                  ))}
                </div>
              )}
            </div>
            {/* Balance Summary */}
            {exchangeNewItems.length > 0 && (() => {
              const newTotal = exchangeNewItems.reduce((s, i) => s + i.unitPrice * i.quantity, 0);
              return (
                <div className="rounded-lg bg-muted/40 px-4 py-3 text-sm">
                  <div className="flex justify-between"><span>New items total (incl. tax)</span><span className="font-medium">{formatCurrency(exchangePreview.data?.totalAmount ?? newTotal)}</span></div>
                  <div className="flex justify-between mt-1">
                    <span>Payment method</span>
                    <Dropdown
                      value={exchangePaymentMethod}
                      onChange={(e) => setExchangePaymentMethod(e.value)}
                      options={['CASH','CARD','BANK_TRANSFER','MOBILE_WALLET'].map((m) => ({ label: m.replace('_',' '), value: m }))}
                      optionLabel="label"
                      optionValue="value"
                      className="p-dropdown-sm"
                    />
                  </div>
                </div>
              );
            })()}
            {/* Notes */}
            <div>
              <Label className="text-xs text-muted-foreground">Notes (optional)</Label>
              <Input value={exchangeNotes} onChange={(e) => setExchangeNotes(e.target.value)} placeholder="Reason for exchange…" className="mt-1 h-8" />
            </div>
          </div>
        )}
      </Dialog>

      {/* Layaway Payment Dialog */}
      <Dialog
        visible={!!layawaySale}
        onHide={() => { setLayawaySale(null); setLayawayAmount(''); }}
        header={
          <div className="flex items-center gap-2">
            <CreditCard className="h-4 w-4 text-primary-text" />
            <span>{t('recordLayawayPayment')}</span>
          </div>
        }
        footer={
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => { setLayawaySale(null); setLayawayAmount(''); }}>{t('cancel')}</Button>
            <Button
              onClick={() => {
                const amt = Number(layawayAmount);
                if (!amt || amt <= 0) { toast.error(t('amountMustBeGreaterThanZero')); return; }
                layawayPaymentMutation.mutate({ id: layawaySale!.id, amount: amt });
              }}
              disabled={layawayPaymentMutation.isPending}
            >
              {layawayPaymentMutation.isPending && <Loader2 className="me-1.5 h-3.5 w-3.5 animate-spin" />}
              {t('recordPayment')}
            </Button>
          </div>
        }
        style={{ width: '28rem', maxWidth: '95vw' }}
        draggable={false}
        resizable={false}
      >
        <div className="space-y-4 py-1">
          {layawaySale && (
            <div className="rounded-lg border bg-muted/30 px-3 py-2 text-sm space-y-1">
              <p className="font-mono font-semibold">{layawaySale.invoiceNumber}</p>
              <div className="flex justify-between text-xs text-muted-foreground">
                <span>{t('total')}</span><span className="font-mono">{formatCurrency(Number(layawaySale.totalAmount))}</span>
              </div>
              <div className="flex justify-between text-xs text-muted-foreground">
                <span>{t('paidSoFar')}</span><span className="font-mono">{formatCurrency(Number(layawaySale.depositAmount ?? 0))}</span>
              </div>
              <div className="flex justify-between text-xs font-medium border-t pt-1">
                <span>{t('remaining')}</span>
                <span className="font-mono text-primary-text">
                  {formatCurrency(Math.max(0, Number(layawaySale.totalAmount) - Number(layawaySale.depositAmount ?? 0)))}
                </span>
              </div>
            </div>
          )}
          <div>
            <Label className="mb-1.5 block">{t('paymentAmount')} <span className="text-destructive-text">*</span></Label>
            <Input
              type="number"
              min={0.01}
              step={0.01}
              value={layawayAmount}
              onChange={(e) => setLayawayAmount(e.target.value)}
              placeholder="0.00"
              className="h-9"
              autoFocus
            />
          </div>
        </div>
      </Dialog>

    </div>
  );
}
